import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Utensils, Fuel, ShoppingBag, Plane, HeartPulse, Receipt, Clapperboard, Circle, Coffee, Home, Gift, Dumbbell, Book, Music, Camera, Pencil, Trash2, Check, X, Plus } from "lucide-react";

const AVAILABLE_ICONS = [
  { key: "utensils", Icon: Utensils }, { key: "fuel", Icon: Fuel }, { key: "shopping-bag", Icon: ShoppingBag },
  { key: "plane", Icon: Plane }, { key: "heart-pulse", Icon: HeartPulse }, { key: "receipt", Icon: Receipt },
  { key: "clapperboard", Icon: Clapperboard }, { key: "circle", Icon: Circle }, { key: "coffee", Icon: Coffee },
  { key: "home", Icon: Home }, { key: "gift", Icon: Gift }, { key: "dumbbell", Icon: Dumbbell },
  { key: "book", Icon: Book }, { key: "music", Icon: Music }, { key: "camera", Icon: Camera },
];
const ICON_MAP = Object.fromEntries(AVAILABLE_ICONS.map(({ key, Icon }) => [key, Icon]));

const AVAILABLE_COLORS = [
  "#f97316", "#3b82f6", "#ec4899", "#06b6d4", "#ef4444",
  "#8b5cf6", "#f59e0b", "#10b981", "#64748b", "#0ea5e9",
];

export default function CategoryManager({ groupId, isAdmin = true }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", icon: "circle", color: "#64748b" });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", icon: "circle", color: "#f97316" });

  const load = async () => {
    if (!groupId) return;
    setLoading(true);
    const { data } = await api.get("/categories", { params: { group_id: groupId } });
    setCategories(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [groupId]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ name: c.name, icon: c.icon || "circle", color: c.color || "#64748b" });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Name required");
    try {
      await api.patch(`/categories/${editingId}`, editForm);
      toast.success("Category updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const create = async () => {
    if (!addForm.name.trim()) return toast.error("Name required");
    try {
      await api.post("/categories", { ...addForm, group_id: groupId });
      toast.success("Category added");
      setAddForm({ name: "", icon: "circle", color: "#f97316" });
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading categories…</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Categories</div>
          <div className="text-xs text-slate-500 mt-0.5">Group-specific — used when adding expenses</div>
        </div>
        {isAdmin && !showAdd && (
          <Button onClick={() => setShowAdd(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 text-sm" data-testid="category-show-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> New
          </Button>
        )}
      </div>

      {showAdd && isAdmin && (
        <CategoryEditor
          value={addForm}
          onChange={setAddForm}
          onSave={create}
          onCancel={() => { setShowAdd(false); setAddForm({ name: "", icon: "circle", color: "#f97316" }); }}
          testidPrefix="category-add"
        />
      )}

      <div className="space-y-2">
        {categories.map((c) => {
          const Icon = ICON_MAP[c.icon] || Circle;
          if (c.id === editingId) {
            return (
              <CategoryEditor
                key={c.id}
                value={editForm}
                onChange={setEditForm}
                onSave={saveEdit}
                onCancel={cancelEdit}
                testidPrefix={`category-edit-${c.id}`}
              />
            );
          }
          return (
            <div key={c.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200" data-testid={`category-row-${c.id}`}>
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${c.color}18` }}>
                <Icon className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900 text-sm">{c.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{c.icon}</div>
              </div>
              {isAdmin && (
                <>
                  <button
                    onClick={() => startEdit(c)}
                    className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500 hover:text-slate-900"
                    data-testid={`category-edit-btn-${c.id}`}
                    aria-label="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => del(c.id)}
                    className="w-8 h-8 rounded-full hover:bg-rose-50 grid place-items-center text-slate-500 hover:text-rose-600"
                    data-testid={`category-delete-btn-${c.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryEditor({ value, onChange, onSave, onCancel, testidPrefix }) {
  return (
    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
      <Input
        placeholder="Category name"
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        className="h-11 rounded-2xl bg-white"
        data-testid={`${testidPrefix}-name`}
        autoFocus
      />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Icon</div>
        <div className="flex flex-wrap gap-1.5">
          {AVAILABLE_ICONS.map(({ key, Icon }) => {
            const active = value.icon === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onChange({ ...value, icon: key })}
                className={`w-9 h-9 rounded-xl grid place-items-center border transition-all ${
                  active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid={`${testidPrefix}-icon-${key}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Color</div>
        <div className="flex flex-wrap gap-1.5">
          {AVAILABLE_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ ...value, color: c })}
              className={`w-8 h-8 rounded-full border-2 transition-transform active:scale-90 ${
                value.color === c ? "border-slate-900 scale-110" : "border-white"
              }`}
              style={{ background: c }}
              data-testid={`${testidPrefix}-color-${c}`}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button onClick={onSave} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white h-9 px-4 text-sm" data-testid={`${testidPrefix}-save`}>
          <Check className="w-3.5 h-3.5 mr-1" /> Save
        </Button>
        <Button onClick={onCancel} variant="outline" className="rounded-full h-9 px-4 text-sm" data-testid={`${testidPrefix}-cancel`}>
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}
